import type { ComponentType } from "react";
import {
  AlignCenterVerticalIcon,
  Calendar,
  CalendarClock,
  CalendarDays,
  CheckCheck,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  Clock,
  ClockAlert,
  CreditCard,
  DoorOpen,
  Eye,
  FileStack,
  FileText,
  FileUser,
  FolderKanban,
  GitBranch,
  HandHelping,
  LayoutGrid,
  ChartColumn,
  LifeBuoy,
  ListChecks,
  ListTodo,
  Mail,
  Network,
  Palmtree,
  PlusCircle,
  Receipt,
  RefreshCcw,
  Settings,
  ShieldCheck,
  Sliders,
  Split,
  SquareUserRound,
  Star,
  Target,
  Timer,
  ToggleLeft,
  UserCheck,
  UserCog,
  UserPlus,
  UserRoundSearch,
  Users,
  Users2Icon,
  UsersRound,
  UserX,
  Building2,
  CircleHelp,
  FileOutput,
} from "lucide-react";

/**
 * Single source of truth for sidebar iconography.
 *
 * Backend sends only stable `code`s; this registry maps each code to its
 * lucide component (audited against Figma CrewKnit HRMS sidebar). Sizes and
 * colors stay at render sites (Sidebar 17px + sidebar tokens) — only the
 * *choice* of glyph is centralized here.
 *
 * Multitenancy-safe: keyed by global code, never by tenant/name/order.
 * Unknown codes render the neutral fallback and warn once (with code) so new
 * backend modules surface in logs instead of crashing render.
 */

type IconComponent = ComponentType<{ className?: string }>;

const warnedCodes = new Set<string>();

function warnOnce(code: string): void {
  if (warnedCodes.has(code)) return;
  warnedCodes.add(code);
  // eslint-disable-next-line no-console
  console.warn(
    `[sidebarIcons] no icon mapped for code "${code}" — fallback used`,
  );
}

const SUBMODULE_ICONS: Record<string, IconComponent> = {
  LEAVE_BALANCES: CreditCard,
  ADMIN_CONFIG_ACCESS_RIGHTS: ShieldCheck,
  ACCESS_RIGHT: ShieldCheck,
  ADMIN_CONFIG_MASTER: ToggleLeft,
  FEATURE_FLAGS: ToggleLeft,
  ADMIN_HOLIDAY_MANAGEMENT: CalendarDays,
  HOLIDAY_MANAGEMENT: CalendarDays,
  MASTER_CONFIG: AlignCenterVerticalIcon,
  ADMIN_KPI_ASSIGNMENTS: ListTodo,
  KPI_MANAGEMENT: ListTodo,
  EMPLOYEE_SERVICE: SquareUserRound,
  ADMIN_PAYROLL: Receipt,
  USER_APPLY_LEAVE: Palmtree,
  APPLY_LEAVE: Palmtree,
  USER_ATTENDANCE_REGULARIZATION: Clock,
  MY_ATTENDANCE: CalendarClock,
  APPLY_AR: Clock,
  ADMIN_REGULARIZATION_MANAGEMENT: ListChecks,
  REGULARIZATION_MANAGEMENT: ListChecks,
  TEAM_AR_APPROVALS: ListChecks,
  ADMIN_LEAVE_APPROVALS: CheckCheck,
  LEAVE_APPROVALS: CheckCheck,
  TEAM_LEAVE_APPROVALS: CheckCheck,
  ADMIN_CREATE_LEAVE: PlusCircle,
  USER_MY_TIMESHEETS: Timer,
  MY_TIMESHEETS: Timer,
  ADMIN_TIMESHEET_APPROVAL: ListChecks,
  TEAM_TIMESHEET_APPROVALS: ListChecks,
  ADMIN_TIMESHEET_MANAGEMENT: ListChecks,
  TIMESHEET_MANAGEMENT: ListChecks,
  USER_MY_PERFORMANCE: UserRoundSearch,
  MY_PERFORMANCE: UserRoundSearch,
  ADMIN_KPI_SCORING: Target,
  ADMIN_REPORTS_ATTENDANCE: Clock,
  REPORTS_ATTENDANCE: Clock,
  ADMIN_REPORTS_REGULARIZATION: RefreshCcw,
  REPORTS_REGULARIZATION: RefreshCcw,
  ADMIN_REPORTS_LEAVE_BALANCE: Eye,
  REPORTS_LEAVE_BALANCE: Eye,
  ADMIN_REPORTS_DYNAMIC: Sliders,
  REPORTS_DYNAMIC: Sliders,
  USER_SURVEYS: ClipboardList,
  MY_SURVEYS: ClipboardList,
  ADMIN_SURVEY_CAMPAIGNS: CreditCard,
  ADMIN_SURVEY_RESPONSES: CheckSquare,
  SURVEY_RESPONSES: CheckSquare,
  SURVEY_MANAGEMENT: ClipboardPen,
  PROJECT_MANAGEMENT: FolderKanban,
  RESOURCE_MANAGEMENT: LayoutGrid,
  PROJECT_ANALYTICS: ChartColumn,
  ORGANIZATION_ONBOARDING: HandHelping,
  MANAGE_WORKFLOW: GitBranch,
  MANAGE_GROUPS: UsersRound,
  REFERRAL: UserPlus,
  REFERRAL_TRACKING: UserPlus,
  REFERRAL_MANAGEMENT: UserCog,
  BILLING: CreditCard,
  LICENSE: CreditCard,
  MY_REFERRALS: Users2Icon,
  WEEK_OFF_CONFIG: Calendar,
  ORGANIZATION_CONFIG: Building2,
  RESIGNATION: FileOutput,
  MY_EXIT_VIEW: Eye,
  MY_EXIT_MANAGE: ClipboardPen,
  EXIT_APPROVALS_VIEW: ClipboardCheck,
  EXIT_APPROVALS_MANAGE: ClipboardCheck,
  EXIT_APPROVALS: ClipboardCheck,
  EXIT_SETTLEMENT_VIEW: Eye,
  EXIT_SETTLEMENT_MANAGE: Receipt,
  TERMINATION_VIEW: UserX,
  TERMINATION_INITIATE_MANAGE: UserX,
  TERMINATION_APPROVALS_MANAGE: UserX,
  HELP_CENTER: LifeBuoy,
  HELP_CENTER_ADMIN_VIEW: CircleHelp,
  EMAIL_TEMPLATES: Mail,
  EMAIL_CONFIG: Settings,
  ORG_CHART: Network,
};

const MODULE_ICONS: Record<string, IconComponent> = {
  ADMIN_CONFIG: Settings,
  LEAVE_ATTENDANCE: Calendar,
  TIMESHEET: Clock,
  PERFORMANCE: Star,
  ADMIN_REPORTS: FileUser,
  REPORTS: FileText,
  SURVEYS: FileStack,
  RESOURCE_ALLOCATION: Network,
  WORKFLOW: Split,
  ORGANIZATION_ONBOARDING: UserCog,
  REFERRAL: UserPlus,
  BILLING_LICENSE: CreditCard,
  CONFIG_MANAGEMENT: Settings,
  SEPARATION: DoorOpen,
  TERMINATION: DoorOpen,
  HELP_CENTER: HandHelping,
  EMAIL_MANAGEMENT: Mail,
  ORG_CHART: Network,
};

/** Resolve a submodule code; falls back to FileText (existing behavior). */
export function resolveSidebarSubmoduleIcon(code: string): IconComponent {
  const found = SUBMODULE_ICONS[code];
  if (!found) warnOnce(code);
  return found ?? FileText;
}

/** Resolve a module code; null when unknown (existing behavior). */
export function resolveSidebarModuleIcon(
  code: string,
): { icon: IconComponent } | null {
  const found = MODULE_ICONS[code];
  if (!found) warnOnce(code);
  return found ? { icon: found } : null;
}

/**
 * Dashboard concepts (Figma admin-dashboard spec). Cards are hand-built, not
 * backend-keyed, so concepts — not codes — are the keys here. Same fallback
 * and warn discipline as the sidebar maps.
 */
const DASHBOARD_ICONS: Record<string, IconComponent> = {
  "total-employees": Users,
  "active-today": UserCheck,
  "on-leave": Palmtree,
  "pending-approvals": ClockAlert,
  "approval-leave": Palmtree,
  "approval-regularization": Clock,
  "approval-timesheet": Timer,
};

/** Resolve a dashboard concept; falls back to FileText. */
export function resolveDashboardIcon(concept: string): IconComponent {
  const found = DASHBOARD_ICONS[concept];
  if (!found) warnOnce(`dashboard:${concept}`);
  return found ?? FileText;
}
